/**
 * Marketer-view band colours.
 *
 * Decision (SYR-68): keep our existing 6-band classification labels in copy
 * (Unbelievably Strong / Excellent / Good / Neutral / Needs Attention /
 * Priority Concern), but collapse colour to Adir's 3 buckets for visual
 * impact. We reuse the same hex palette as the rest of brand-equity-app
 * (BenchmarkingPage, BrandEquityVisualizationWrapper) — not Adir's earthier
 * tones — so the marketer view feels native to the dashboard.
 *
 * Band thresholds use the BIPOLAR scale (-100..+100). Negative-bipolar
 * categories (Price Deals) are handled by the API classifier; we trust the
 * classification label that comes back and map it via `BAND_BY_LABEL`.
 */

import type { Band } from '../types';

export const BAND_PALETTE = {
  strong: {
    fg: '#16a34a', // green-600 — matches benchmarking "Excellent"
    bg: '#dcfce7', // green-100
    line: '#86efac', // green-300
  },
  watch: {
    fg: '#d97706', // amber-600 — matches benchmarking "Fair"
    bg: '#fef3c7', // amber-100
    line: '#fcd34d', // amber-300
  },
  weak: {
    fg: '#dc2626', // red-600 — matches benchmarking "Critical"
    bg: '#fee2e2', // red-100
    line: '#fca5a5', // red-300
  },
  na: {
    fg: '#6b7280', // gray-500
    bg: '#f3f4f6', // gray-100
    line: '#d1d5db', // gray-300
  },
} as const satisfies Record<Band, { fg: string; bg: string; line: string }>;

/**
 * Red→amber→green gradient for the BE meter. Matches the standard meter in
 * `BrandEquityVisualizationWrapper.tsx` — verbatim hex stops.
 */
export const METER_GRADIENT =
  'linear-gradient(to right, #ef4444 0%, #f59e0b 25%, #eab308 40%, #84cc16 60%, #22c55e 75%, #10b981 100%)';

/**
 * Price Deals variant — the element lives on a -100..0 scale (zero at the
 * right edge), so the gradient is re-stopped to match the v3 orbital's
 * Price Deals meter (`BrandEquityVisualizationV3.tsx`) verbatim.
 */
export const METER_GRADIENT_NEGATIVE =
  'linear-gradient(to right, #ef4444 0%, #f59e0b 30%, #eab308 60%, #84cc16 85%, #22c55e 100%)';

/**
 * Map the existing 6-band classification label to our 3-bucket band.
 * `null` (no data) is handled by callers — defaults to `'na'`.
 */
const BAND_BY_LABEL: Readonly<Record<string, Band>> = Object.freeze({
  'Unbelievably Strong': 'strong',
  Excellent: 'strong',
  Good: 'watch',
  Neutral: 'watch',
  'Needs Attention': 'weak',
  'Priority Concern': 'weak',
});

/** Collapse the API's 6-band label to a 3-bucket colour band. */
export function bandFromClassification(classification: string | null): Band {
  if (!classification) return 'na';
  return BAND_BY_LABEL[classification] ?? 'na';
}

/**
 * Score-only fallback when the caller doesn't have the API's classification
 * label handy (e.g. computed aggregate meters). Uses the same boundaries as
 * BIPOLAR_BANDS: strong ≥50, weak < -20, otherwise watch.
 */
export function bandFromScore(value: number | null | undefined): Band {
  if (value == null || Number.isNaN(value)) return 'na';
  if (value >= 50) return 'strong';
  if (value < -20) return 'weak';
  return 'watch';
}

/** Convert a bipolar -100..+100 score into a 0..100 gauge position. */
export function gaugePosition(value: number | null | undefined): number {
  if (value == null || Number.isNaN(value)) return 50;
  return Math.max(0, Math.min(100, (value + 100) / 2));
}

/**
 * Format an ABSOLUTE bipolar score for display: "47%", "-7%", "N/A".
 *
 * Matches the rest of the dashboard's convention: no `+` prefix on positive
 * absolute scores. Negative numbers carry their natural `-`; null is N/A.
 *
 * For deltas (e.g. vs-Benchmark, vs-previous-period), DO include the `+`
 * prefix — that's a separate semantic and the dashboard `MiniOrbitalHero`
 * pattern uses it.
 */
export function formatBipolarScore(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return 'N/A';
  const rounded = Math.round(value);
  return `${rounded}%`;
}
